import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';

interface LearningPathAnalyticsProps {
  dateRange: 'week' | 'month' | 'quarter' | 'year';
  districtId: string;
}

interface LearningPathData {
  pathName: string;
  assignedCount: number;
  startedCount: number;
  completedCount: number;
  averageCompletionTime: number; // in days
}

const LearningPathAnalytics: React.FC<LearningPathAnalyticsProps> = ({ dateRange, districtId }) => {
  const [loading, setLoading] = useState(true);
  const [pathData, setPathData] = useState<LearningPathData[]>([]);
  const [sortBy, setSortBy] = useState<keyof LearningPathData>('assignedCount');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would call an API with the dateRange and districtId parameters
        // For now, we'll generate mock data based on the parameters
        const mockData = generateMockData(dateRange, districtId);
        setPathData(mockData);
      } catch (error) {
        console.error('Error fetching learning path data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, districtId]);

  const generateMockData = (range: string, district: string): LearningPathData[] => {
    const pathNames = [
      'Math Fundamentals K-2',
      'Number Sense Grade 3-5',
      'Algebra Readiness',
      'Geometry Essentials',
      'Fractions and Decimals',
      'Data and Statistics'
    ];
    
    const result: LearningPathData[] = [];
    
    // Base values that will be adjusted based on district, range, and path
    const baseAssignedCount = district === 'all' ? 1000 : 300;
    const baseStartedRatio = 0.8; // 80% of assigned are started
    const baseCompletedRatio = 0.6; // 60% of started are completed
    const baseCompletionTime = 14; // days
    
    // Range factors (longer ranges have more assignments)
    const rangeFactor = range === 'week' ? 1 : 
                        range === 'month' ? 4 : 
                        range === 'quarter' ? 12 : 48;
    
    // Generate data for each path
    for (let i = 0; i < pathNames.length; i++) {
      // Add some variation based on the path index
      const pathFactor = 1 - (i * 0.1); // First paths have higher engagement
      
      // Calculate values with some randomness
      const assignedCount = Math.round(baseAssignedCount * pathFactor * rangeFactor * (0.9 + Math.random() * 0.2));
      const startedCount = Math.round(assignedCount * baseStartedRatio * (0.9 + Math.random() * 0.2));
      const completedCount = Math.round(startedCount * baseCompletedRatio * (0.9 + Math.random() * 0.2));
      const averageCompletionTime = Math.round(baseCompletionTime * (1 + (i * 0.1)) * (0.9 + Math.random() * 0.2));
      
      result.push({
        pathName: pathNames[i],
        assignedCount,
        startedCount,
        completedCount,
        averageCompletionTime
      });
    }
    
    return result;
  };

  const handleSort = (column: keyof LearningPathData) => {
    if (sortBy === column) {
      // Toggle sort direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const sortedData = [...pathData].sort((a, b) => {
    const valueA = a[sortBy];
    const valueB = b[sortBy];
    
    if (valueA < valueB) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate totals for summary
  const totalAssigned = pathData.reduce((sum, data) => sum + data.assignedCount, 0);
  const totalStarted = pathData.reduce((sum, data) => sum + data.startedCount, 0);
  const totalCompleted = pathData.reduce((sum, data) => sum + data.completedCount, 0);
  const avgCompletionTime = pathData.reduce((sum, data) => sum + (data.averageCompletionTime * data.completedCount), 0) / totalCompleted;

  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Completion Rate</div>
          <div className="text-xl font-bold text-gray-800">
            {Math.round((totalCompleted / totalAssigned) * 100)}%
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {totalCompleted.toLocaleString()} of {totalAssigned.toLocaleString()} assigned
          </div>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Avg. Completion Time</div>
          <div className="text-xl font-bold text-gray-800">
            {Math.round(avgCompletionTime)} days
          </div>
          <div className="text-xs text-gray-500 mt-1">
            From assignment to completion
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('pathName')}
              >
                Learning Path
                {sortBy === 'pathName' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('assignedCount')}
              >
                Assigned
                {sortBy === 'assignedCount' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('startedCount')}
              >
                Started
                {sortBy === 'startedCount' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('completedCount')}
              >
                Completed
                {sortBy === 'completedCount' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('averageCompletionTime')}
              >
                Avg. Time
                {sortBy === 'averageCompletionTime' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((data) => (
              <tr key={data.pathName} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {data.pathName}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {data.assignedCount.toLocaleString()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-blue-500 rounded-full h-2" 
                        style={{ width: `${(data.startedCount / data.assignedCount) * 100}%` }}
                      ></div>
                    </div>
                    <span>{Math.round((data.startedCount / data.assignedCount) * 100)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-green-500 rounded-full h-2" 
                        style={{ width: `${(data.completedCount / data.assignedCount) * 100}%` }}
                      ></div>
                    </div>
                    <span>{Math.round((data.completedCount / data.assignedCount) * 100)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {data.averageCompletionTime} days
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6 bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Learning Path Insights</h3>
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            The most successful learning path is <span className="font-medium">{
              pathData.sort((a, b) => (b.completedCount / b.assignedCount) - (a.completedCount / a.assignedCount))[0].pathName
            }</span> with a {Math.round((
              pathData.sort((a, b) => (b.completedCount / b.assignedCount) - (a.completedCount / a.assignedCount))[0].completedCount /
              pathData.sort((a, b) => (b.completedCount / b.assignedCount) - (a.completedCount / a.assignedCount))[0].assignedCount
            ) * 100)}% completion rate.
          </p>
          <p>
            The average time to complete a learning path is {Math.round(avgCompletionTime)} days, with {
              pathData.sort((a, b) => a.averageCompletionTime - b.averageCompletionTime)[0].pathName
            } being completed the fastest at {
              pathData.sort((a, b) => a.averageCompletionTime - b.averageCompletionTime)[0].averageCompletionTime
            } days on average.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LearningPathAnalytics;
